import { Component, ElementRef, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentChatService } from './agent-chat.service';
import { LocalStorageService } from '../services/local-storage.service';
import { USER_PERMISSIONS } from '../app.constants';

/**
 * Slide-in chat panel. Kept deliberately small — it reads everything from
 * AgentChatService so the component file stays focused on presentation.
 * Mounted once in AdminLayoutComponent.
 */
@Component({
  selector: 'app-agent-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-chat.component.html',
  styleUrl: './agent-chat.component.css',
})
export class AgentChatComponent {
  readonly chat = inject(AgentChatService);
  private readonly auth = inject(LocalStorageService);

  question = '';
  // Only admin-ish roles see the executed SQL block in the UI by default.
  // Everyone else still gets the answer + rendered table.
  readonly canSeeSql = this.auth.checkPermission(
    USER_PERMISSIONS.AGENT?.AGENT_VIEW_SQL ?? 'agent_view_sql'
  );

  @ViewChild('scrollAnchor') scrollAnchor?: ElementRef<HTMLDivElement>;

  constructor() {
    // Auto-scroll to the latest message whenever the list changes.
    effect(() => {
      this.chat.messages();
      queueMicrotask(() => {
        this.scrollAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  send() {
    const q = this.question;
    this.question = '';
    void this.chat.ask(q);
  }

  onKey(event: KeyboardEvent) {
    // Cmd/Ctrl+Enter or plain Enter (without shift) submits.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  columnsOf(rows: any[] | undefined): string[] {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  }
}
